import React, { useEffect, useState, useMemo } from "react";
import { useStore } from "../../../stores/store.ts";

import HomeViewModel from "@arcgis/core/widgets/Home/HomeViewModel";
//@ts-expect-error
import backIcon from "./icons/back.png";
//@ts-expect-error
import forwardIcon from "./icons/forward.png";
//@ts-expect-error
import fullViewIcon from "./icons/fullView.png";
//@ts-expect-error
import panIcon from "./icons/pan.png";
//@ts-expect-error
import zoomInIcon from "./icons/zoomIn.png";
//@ts-expect-error
import zoomOutIcon from "./icons/zoomOut.png";
import ToolbarButton from "../ToolbarButton.tsx";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";

interface NavigationDropdownProps {
  active: boolean;
  onClick: () => void;
}

interface NavigationOption {
  label: string;
  key: string;
  icon: string;
  action: () => void;
  isActive?: () => boolean;
}

export default (props: NavigationDropdownProps) => {
  const {
    mapLoaded,
    mapView,
    extentHistory,
    navigationMode,
    setNavigationMode,
    bufferLayer,
    config,
  } = useStore();
  const [activeExtentIndex, setActiveExtentIndex] = useState(-1);

  useEffect(() => {
    if (!mapLoaded || !mapView) return;
    const dragHandler = mapView.on("drag", (e) => {
      if (navigationMode !== "pan") {
        e.stopPropagation();
      }
    });
    let sketchViewModel: SketchViewModel;

    if (navigationMode !== "pan") {
      sketchViewModel = new SketchViewModel({
        view: mapView,
        layer: bufferLayer as any,
        polygonSymbol: {
          type: "simple-fill",
          color: "rgba(0, 0, 0, 0.4)",
          style: "solid",
          outline: {
            color: "red",
            width: 1,
          },
        },
        defaultUpdateOptions: {
          toggleToolOnClick: false,
        },
      });
      sketchViewModel.create("rectangle");
      sketchViewModel.on("create", (e) => {
        if (e.state === "complete") {
          if (navigationMode === "zoomIn") {
            mapView.goTo(e.graphic.geometry);
          } else if (navigationMode === "zoomOut") {
            mapView.goTo({
              center: e.graphic.geometry.extent.center,
              zoom: mapView.zoom - 1,
            });
          }
          bufferLayer?.remove(e.graphic);
          sketchViewModel.create("rectangle");
        }
      });
    }

    return () => {
      dragHandler.remove();
      sketchViewModel?.destroy();
    };
  }, [navigationMode, mapView, mapLoaded]);

  const homeViewModel = useMemo(() => {
    if (!mapView) return;
    return new HomeViewModel({ view: mapView });
  }, [mapView]);

  const navigationOptions: NavigationOption[] = useMemo(
    () => [
      {
        label: "Zoom In",
        key: "zoomIn",
        icon: zoomInIcon,
        action: () => {
          setNavigationMode("zoomIn");
        },
      },
      {
        label: "Zoom Out",
        key: "zoomOut",
        icon: zoomOutIcon,
        action: () => {
          setNavigationMode("zoomOut");
        },
      },

      {
        label: "Pan",
        key: "pan",
        icon: panIcon,
        action: () => {
          setNavigationMode("pan");
        },
      },
      {
        label: "Full View",
        key: "fullView",
        icon: fullViewIcon,
        action: () => {
          homeViewModel?.go();
        },
      },
      {
        label: "Back",
        key: "back",
        icon: backIcon,
        action: () => {
          const newIndex =
            activeExtentIndex === -1
              ? extentHistory.length - 2
              : activeExtentIndex - 1;
          if (newIndex >= 0) {
            setActiveExtentIndex(newIndex);
            mapView?.goTo(extentHistory[newIndex]);
          }
        },
        isActive: () => {
          return extentHistory.length > 1 && activeExtentIndex !== 0;
        },
      },
      {
        label: "Forward",
        key: "forward",
        icon: forwardIcon,
        action: () => {
          const newIndex = activeExtentIndex + 1;
          if (newIndex < extentHistory.length) {
            setActiveExtentIndex(newIndex);
            mapView?.goTo(extentHistory[newIndex]);
          }
        },
        isActive: () => {
          return (
            extentHistory.length > 1 &&
            activeExtentIndex !== extentHistory.length - 1 &&
            activeExtentIndex !== -1
          );
        },
      },
    ],
    [extentHistory, activeExtentIndex, mapView, navigationMode]
  );

  return (
    <>
      {props.active && (
        <div className="absolute w-max bg-white rounded-md z-50 max-h-72 overflow-auto ">
          <div
            style={{
              backgroundImage:
                config?.toolbarOptions.toolbarBackgroundImage ||
                "linear-gradient(to bottom, #d9af66, #a58752)",
            }}
            className="h-10 overflow-visible shadow-sm flex items-center w-auto"
          >
            {navigationOptions.map((option, i) => (
              <ToolbarButton
                id={"navigation" + i}
                key={"navigation" + i}
                active={navigationMode === option.key}
                label={option.label}
                onClick={() => option.action()}
                icon={option.icon}
                highlightColor={
                  config?.toolbarOptions.toolbarButtonHighlightColor
                }
                hideLabelOnTablet={true}
                textColor={config?.toolbarOptions.toolbarButtonTextColor}
                disabled={
                  option.isActive !== undefined ? !option.isActive() : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};
